/**
 * ZoneAutoGenerateDialog — NVR 용량 기준 구역 자동 생성 입력.
 *
 * 구역은 NVR 1대가 담당하는 묶음이라 용량이 곧 구역 크기의 상한이다. 여기서
 * 받은 값으로 미배정 CCTV 를 용량 한도까지 최대로 묶고(원), 이어서 구역별로
 * CCTV 이름을 순차 부여한다. 이름 접두어는 기존 이름에서 추론해 채워 두고
 * 사용자가 고칠 수 있게 한다.
 */
import React, { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'

/** `P1_B1` — CCTV 이름 `P{동}_B{층}_{구역}_{순번}` 의 앞 두 토막. */
const PREFIX_RE = /^P\d+_B\d+$/

/** 'capacity' = NVR 용량으로 추정, 'nvrTemplate' = NVR 등록 템플릿 엑셀이 정답. */
export type ZoneAutoGenerateMode = 'capacity' | 'nvrTemplate'

export interface ZoneAutoGenerateValues {
  mode: ZoneAutoGenerateMode
  /** mode === 'nvrTemplate' 일 때 읽을 .xls 파일들. */
  files: File[]
  nvrGb: number
  omniGb: number
  onewayGb: number
  /** NVR 1대 최대 채널 수 */
  maxChannels: number
  /** 만들 구역 수 — 비우면 용량·채널 한도까지 최대로 채운다. */
  targetZoneCount: number | null
  /** 빈 문자열이면 이름은 부여하지 않는다. */
  namePrefix: string
  /** true 면 기존 구역을 모두 지우고 카메라 소속·이름을 초기화한 뒤 새로 만든다. */
  rebuild: boolean
}

interface Props {
  open: boolean
  /** 구역 미배정 카메라 수 — 입력값이 말이 되는지 바로 보이게 한다. */
  pending: { omni: number; oneway: number }
  /** 맵 전체 카메라 수 — "다시 만들기" 를 켜면 이 수가 대상이 된다. */
  total: { omni: number; oneway: number }
  /** 현재 맵의 구역 수 (다시 만들기 안내용) */
  existingZoneCount: number
  /** 기존 CCTV 이름에서 추론한 접두어. 없으면 빈 문자열. */
  defaultPrefix: string
  busy: boolean
  onRun: (values: ZoneAutoGenerateValues) => void
  onClose: () => void
}

function parsePositive(text: string): number | null {
  const value = Number(text.trim())
  return Number.isFinite(value) && value > 0 ? value : null
}

function parsePositiveInt(text: string): number | null {
  const value = parsePositive(text)
  return value !== null && Number.isInteger(value) ? value : null
}

const DEFAULT_MAX_CHANNELS = '36'

export const ZoneAutoGenerateDialog: React.FC<Props> = ({
  open,
  pending,
  total,
  existingZoneCount,
  defaultPrefix,
  busy,
  onRun,
  onClose,
}) => {
  const [nvr, setNvr] = useState('')
  const [omni, setOmni] = useState('')
  const [oneway, setOneway] = useState('')
  const [channels, setChannels] = useState(DEFAULT_MAX_CHANNELS)
  const [zoneCount, setZoneCount] = useState('')
  const [rebuild, setRebuild] = useState(false)
  const [prefix, setPrefix] = useState(defaultPrefix)
  const [touched, setTouched] = useState(false)
  const [mode, setMode] = useState<ZoneAutoGenerateMode>('capacity')
  const [files, setFiles] = useState<File[]>([])

  useEffect(() => {
    if (open) {
      setPrefix(defaultPrefix)
      setRebuild(false)
      setTouched(false)
      setFiles([])
    }
  }, [open, defaultPrefix])

  const nvrGb = parsePositive(nvr)
  const omniGb = parsePositive(omni)
  const onewayGb = parsePositive(oneway)
  const maxChannels = parsePositiveInt(channels)
  const targetZoneCount = zoneCount.trim() === '' ? null : parsePositiveInt(zoneCount)
  const zoneCountValid = zoneCount.trim() === '' || targetZoneCount !== null
  const trimmedPrefix = prefix.trim()
  const prefixValid = trimmedPrefix === '' || PREFIX_RE.test(trimmedPrefix)
  const capacityValid =
    nvrGb !== null && omniGb !== null && onewayGb !== null && maxChannels !== null
  const valid =
    mode === 'nvrTemplate' ? files.length > 0 : capacityValid && prefixValid && zoneCountValid

  // 다시 만들기면 맵 전체 카메라가 대상, 아니면 미배정만.
  const target = rebuild ? total : pending
  const targetCount = target.omni + target.oneway

  // 입력값 검산 — 대당 용량이 NVR 용량보다 크면 그 종류는 1대도 못 들어간다.
  const totalGb =
    capacityValid ? target.omni * omniGb! + target.oneway * onewayGb! : null
  const minZonesByGb = totalGb !== null && nvrGb ? Math.ceil(totalGb / nvrGb) : null
  const minZonesByCh = maxChannels ? Math.ceil(targetCount / maxChannels) : null
  const minZones =
    minZonesByGb !== null && minZonesByCh !== null ? Math.max(minZonesByGb, minZonesByCh) : null
  const tooBig =
    capacityValid &&
    ((target.omni > 0 && omniGb! > nvrGb!) ||
      (target.oneway > 0 && onewayGb! > nvrGb!))
  const zoneCountTooSmall =
    targetZoneCount !== null && minZones !== null && targetZoneCount < minZones

  const handleRun = () => {
    if (!valid || busy) return
    onRun({
      mode,
      files,
      nvrGb: nvrGb ?? 0,
      omniGb: omniGb ?? 0,
      onewayGb: onewayGb ?? 0,
      maxChannels: maxChannels ?? 0,
      targetZoneCount,
      namePrefix: trimmedPrefix,
      rebuild,
    })
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>구역 자동 생성</DialogTitle>
      <DialogContent>
        <Tabs
          value={mode}
          onChange={(_, v: ZoneAutoGenerateMode) => setMode(v)}
          sx={{ mb: 2 }}
          variant="fullWidth"
        >
          <Tab value="capacity" label="NVR 용량 기준" disabled={busy} />
          <Tab value="nvrTemplate" label="NVR 템플릿 엑셀" disabled={busy} />
        </Tabs>

        {mode === 'nvrTemplate' ? (
          <Stack spacing={2}>
            <Alert severity="info">
              NVR 원격등록 템플릿(.xls)의 <strong>IP</strong> 로 배치된 CCTV 를 찾아{' '}
              <strong>CH NAME</strong> 으로 이름을 바꾸고, 이름의 구역 번호끼리 묶어
              구역을 그립니다. 여러 파일(NVR 여러 대)을 한 번에 선택하세요. 다른 층
              카메라는 이 맵에 없으므로 자동으로 빠집니다.
            </Alert>
            <Button variant="outlined" component="label" disabled={busy}>
              템플릿 파일 선택
              <input
                hidden
                type="file"
                multiple
                accept=".xls,.xlsx"
                onChange={e => setFiles(Array.from(e.target.files ?? []))}
              />
            </Button>
            <Typography variant="body2" color={files.length ? 'text.primary' : 'text.secondary'}>
              {files.length === 0
                ? '선택된 파일 없음'
                : `${files.length}개 선택: ${files.map(f => f.name).join(', ')}`}
            </Typography>
            <Alert severity="warning">
              같은 번호의 기존 구역은 지우고 새로 만듭니다. CCTV 에 IP 가 먼저
              매핑돼 있어야 합니다(📷 CCTV IP).
            </Alert>
          </Stack>
        ) : (
        <>
        <Alert severity="info" sx={{ mb: 2 }}>
          {rebuild ? '맵 전체 CCTV' : '구역 미배정 CCTV'}{' '}
          <strong>
            전방위 {target.omni}대 · 단방향 {target.oneway}대
          </strong>
          를 NVR 용량·채널 한도 안에서 묶어 구역(원)을 만들고, 구역별로 CCTV
          이름을 순차 부여합니다.{' '}
          {rebuild ? (
            <strong>기존 구역 {existingZoneCount}개를 지우고 소속·이름을 전부 새로 부여합니다.</strong>
          ) : (
            <strong>이미 구역·이름이 있는 CCTV 는 그대로 둡니다.</strong>
          )}
        </Alert>

        <Stack spacing={2}>
          <TextField
            autoFocus
            fullWidth
            type="number"
            label="NVR 1대 저장 용량 (카메라와 같은 단위)"
            placeholder="예: 40"
            value={nvr}
            onChange={e => {
              setNvr(e.target.value)
              setTouched(true)
            }}
            error={touched && nvrGb === null}
            disabled={busy}
          />
          <TextField
            fullWidth
            type="number"
            label="전방위 카메라 1대 용량"
            placeholder="예: 0.91"
            value={omni}
            onChange={e => {
              setOmni(e.target.value)
              setTouched(true)
            }}
            error={touched && omniGb === null}
            disabled={busy}
          />
          <TextField
            fullWidth
            type="number"
            label="단방향 카메라 1대 용량"
            placeholder="예: 0.61"
            value={oneway}
            onChange={e => {
              setOneway(e.target.value)
              setTouched(true)
            }}
            error={touched && onewayGb === null}
            disabled={busy}
          />
          <TextField
            fullWidth
            type="number"
            label="NVR 1대 최대 채널 수"
            value={channels}
            onChange={e => {
              setChannels(e.target.value)
              setTouched(true)
            }}
            error={touched && maxChannels === null}
            disabled={busy}
          />
          <TextField
            fullWidth
            type="number"
            label="구역 수 (선택)"
            placeholder="비우면 한도까지 최대로 채움"
            value={zoneCount}
            onChange={e => {
              setZoneCount(e.target.value)
              setTouched(true)
            }}
            error={touched && !zoneCountValid}
            helperText={
              targetZoneCount !== null && targetCount > 0
                ? `구역당 약 ${Math.ceil(targetCount / targetZoneCount)}대`
                : undefined
            }
            disabled={busy}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={rebuild}
                onChange={e => setRebuild(e.target.checked)}
                disabled={busy}
              />
            }
            label={`기존 구역 ${existingZoneCount}개 삭제 후 전부 다시 만들기 (CCTV 소속·이름 초기화)`}
          />
          <TextField
            fullWidth
            label="CCTV 이름 접두어"
            placeholder="예: P1_B1 (비우면 이름 부여 생략)"
            value={prefix}
            onChange={e => {
              setPrefix(e.target.value)
              setTouched(true)
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') handleRun()
            }}
            error={touched && !prefixValid}
            helperText={
              !prefixValid
                ? 'P{동}_B{층} 형식으로 입력하세요 (예: P1_B1)'
                : trimmedPrefix === ''
                  ? '접두어가 없어 이름은 부여되지 않습니다 — 구역만 생성됩니다'
                  : `새 이름: ${trimmedPrefix}_{구역}_{순번}`
            }
            disabled={busy}
          />
        </Stack>

        {tooBig && (
          <Alert severity="error" sx={{ mt: 2 }}>
            카메라 1대 용량이 NVR 용량보다 큽니다 — 그 종류는 한 대도 배정되지
            않습니다.
          </Alert>
        )}
        {!tooBig && minZones !== null && (
          <Alert severity={zoneCountTooSmall ? 'warning' : 'success'} sx={{ mt: 2 }}>
            총 {totalGb!.toLocaleString()} (용량 {minZonesByGb}구역 · 채널 {minZonesByCh}구역) → 최소{' '}
            {minZones}구역
            {zoneCountTooSmall &&
              ` — 지정한 ${targetZoneCount}구역으로는 부족해 한도에서 끊겨 더 생깁니다`}
          </Alert>
        )}
        </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          취소
        </Button>
        <Button variant="contained" onClick={handleRun} disabled={!valid || busy}>
          {busy ? '생성 중...' : '구역 생성'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ZoneAutoGenerateDialog
